import { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Divider from "@mui/material/Divider";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";

import {
  CreateManualCustomerCall,
  GetCrmTemplatesCall,
  SaveCrmTemplateCall,
  DeleteCrmTemplateCall,
  SendCrmEmailCall,
} from "../../redux/api/demoRequest.api";

const STEPS = [
  { value: "first", label: "First email" },
  { value: "reminder_1", label: "Reminder 1" },
  { value: "reminder_2", label: "Reminder 2" },
  { value: "reminder_3", label: "Reminder 3" },
];

const TOKEN_CHIPS = ["{{first_name}}", "{{last_name}}", "{{university}}", "{{email}}", "{{teaching_area}}"];

const EMPTY_CUSTOMER = {
  first_name: "",
  last_name: "",
  work_email: "",
  phone: "",
  university_name: "",
  country: "",
  academic_role: "",
  teaching_area: "",
  class_size: "",
  main_interest: "",
  main_goal: "",
  time_preference: "",
  pilot: "",
  notes: "",
};

const EMPTY_DRAFT = { id: null, name: "", subject: "", html: "", step: "first" };

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    minHeight: 56,
    borderRadius: 2,
  },
  "& .MuiOutlinedInput-input": {
    py: "14px",
    color: "#000000",
    fontWeight: 600,
  },
  "& .MuiInputLabel-root": {
    fontWeight: 700,
    color: "ink.body",
  },
};

/**
 * Soft-UI theme collapses MUI Select height (`padding: 0 !important`) and hides
 * the chevron. Use a native <select> so min-height / padding stay reliable.
 */
function CrmSelect({ label, value, onChange, options, emptyLabel }) {
  const selectId = `crm-select-${label.replace(/\s+/g, "-").toLowerCase()}`;
  return (
    <Box sx={{ position: "relative", width: "100%", mt: 0.75 }}>
      <Typography
        component="label"
        htmlFor={selectId}
        variant="caption"
        sx={{
          position: "absolute",
          top: -9,
          left: 12,
          zIndex: 1,
          px: 0.75,
          bgcolor: "background.card",
          color: "#000000",
          fontWeight: 700,
          lineHeight: 1.2,
        }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          minHeight: 56,
          borderRadius: "10px",
          border: "1.5px solid #94a3b8",
          bgcolor: "background.card",
          transition: "border-color 0.15s ease, box-shadow 0.15s ease",
          "&:focus-within": {
            borderColor: "#1A73E8",
            boxShadow: "0 0 0 3px rgba(26, 115, 232, 0.16)",
          },
        }}
      >
        <Box
          component="select"
          id={selectId}
          value={value ?? ""}
          onChange={onChange}
          sx={{
            appearance: "none",
            WebkitAppearance: "none",
            MozAppearance: "none",
            width: "100%",
            minHeight: 56,
            height: 56,
            m: 0,
            border: 0,
            outline: "none",
            bgcolor: "transparent",
            pl: 1.75,
            pr: 5,
            py: 0,
            fontSize: "0.95rem",
            fontWeight: 700,
            fontFamily: "inherit",
            lineHeight: "56px",
            color: "#000000",
            cursor: "pointer",
          }}
        >
          {emptyLabel ? (
            <option value="" disabled={Boolean(value)}>
              {emptyLabel}
            </option>
          ) : null}
          {options.map((opt) => (
            <option key={opt.value} value={opt.value} disabled={Boolean(opt.disabled)}>
              {opt.label}
            </option>
          ))}
        </Box>
        <ExpandMoreRoundedIcon
          sx={{
            position: "absolute",
            right: 12,
            pointerEvents: "none",
            color: "#000000",
            fontSize: 26,
          }}
        />
      </Box>
    </Box>
  );
}

function stepLabel(value) {
  return STEPS.find((s) => s.value === value)?.label || value || "—";
}

export function AddCustomerDialog({ open, onClose, onCreated }) {
  const [form, setForm] = useState(EMPTY_CUSTOMER);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setForm(EMPTY_CUSTOMER);
      setError("");
    }
  }, [open]);

  const set = (key) => (event) => setForm((prev) => ({ ...prev, [key]: event.target.value }));

  const save = async () => {
    setSaving(true);
    setError("");
    const res = await CreateManualCustomerCall(form);
    setSaving(false);
    if (res?.success) {
      onCreated(res.data);
      onClose();
      return;
    }
    setError(res?.message || "Could not add this customer.");
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Add customer</DialogTitle>
      <DialogContent>
        <Stack spacing={1.5} sx={{ mt: 0.5 }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <TextField label="First name" value={form.first_name} onChange={set("first_name")} fullWidth sx={fieldSx} />
            <TextField label="Last name" value={form.last_name} onChange={set("last_name")} fullWidth sx={fieldSx} />
          </Stack>
          <TextField label="Work email" type="email" required value={form.work_email} onChange={set("work_email")} fullWidth sx={fieldSx} />
          <TextField label="Phone" value={form.phone} onChange={set("phone")} fullWidth sx={fieldSx} />
          <TextField label="University" value={form.university_name} onChange={set("university_name")} fullWidth sx={fieldSx} />
          <TextField label="Country" value={form.country} onChange={set("country")} fullWidth helperText="ISO code, for example IT" sx={fieldSx} />
          <TextField label="Academic role" value={form.academic_role} onChange={set("academic_role")} fullWidth sx={fieldSx} />
          <TextField label="Teaching area" value={form.teaching_area} onChange={set("teaching_area")} fullWidth sx={fieldSx} />
          <TextField label="Class size" value={form.class_size} onChange={set("class_size")} fullWidth sx={fieldSx} />
          <TextField label="Main interest" value={form.main_interest} onChange={set("main_interest")} fullWidth sx={fieldSx} />
          <TextField label="Main goal" value={form.main_goal} onChange={set("main_goal")} fullWidth multiline minRows={2} />
          <TextField label="Time preference" value={form.time_preference} onChange={set("time_preference")} fullWidth sx={fieldSx} />
          <TextField label="Pilot interest" value={form.pilot} onChange={set("pilot")} fullWidth sx={fieldSx} />
          <TextField label="Notes" value={form.notes} onChange={set("notes")} fullWidth multiline minRows={2} />
          {error ? (
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ color: "#334155 !important", fontWeight: 700 }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={saving || !form.work_email.trim()}
          onClick={save}
          sx={{ fontWeight: 700, color: "#fff !important" }}
        >
          {saving ? "Saving…" : "Add customer"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function TemplatesDialog({ open, onClose }) {
  const [templates, setTemplates] = useState([]);
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [error, setError] = useState("");

  const load = async () => {
    const res = await GetCrmTemplatesCall();
    setTemplates(Array.isArray(res?.data) ? res.data : []);
  };

  useEffect(() => {
    if (open) {
      setDraft(EMPTY_DRAFT);
      setError("");
      load();
    }
  }, [open]);

  const edit = (template) => {
    setDraft({
      id: template.id,
      name: template.name || "",
      subject: template.subject || "",
      html: template.html || "",
      step: template.step || "first",
    });
  };

  const save = async () => {
    setError("");
    const res = await SaveCrmTemplateCall(draft);
    if (!res?.success) {
      setError(res?.message || "Could not save the template.");
      return;
    }
    setDraft(EMPTY_DRAFT);
    load();
  };

  const remove = async (id) => {
    await DeleteCrmTemplateCall(id);
    if (draft.id === id) setDraft(EMPTY_DRAFT);
    load();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ pb: 1 }}>Email templates</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Sent from samira@microlab.ink, with saeid@microlab.ink on cc.
        </Typography>
        <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mb: 2.5 }}>
          {TOKEN_CHIPS.map((token) => (
            <Chip
              key={token}
              size="small"
              label={token}
              variant="outlined"
              sx={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 12 }}
            />
          ))}
        </Stack>

        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
          Saved templates
        </Typography>
        <Stack spacing={0.75} sx={{ mb: 2.5 }}>
          {templates.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No templates yet. Add one below.
            </Typography>
          ) : (
            templates.map((template) => {
              const active = draft.id === template.id;
              return (
                <Box
                  key={template.id}
                  display="flex"
                  alignItems="center"
                  gap={1}
                  sx={{
                    px: 1.25,
                    py: 0.75,
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: active ? "primary.main" : "rgba(148, 163, 184, 0.28)",
                    bgcolor: active ? "rgba(25, 118, 210, 0.06)" : "background.paper",
                    cursor: "pointer",
                    "&:hover": { borderColor: "primary.light", bgcolor: "rgba(25, 118, 210, 0.04)" },
                  }}
                  onClick={() => edit(template)}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600} noWrap>
                      {template.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {stepLabel(template.step)}
                    </Typography>
                  </Box>
                  <Chip size="small" label={stepLabel(template.step)} variant="outlined" sx={{ display: { xs: "none", sm: "inline-flex" } }} />
                  <IconButton
                    size="small"
                    aria-label="Delete template"
                    onClick={(e) => {
                      e.stopPropagation();
                      remove(template.id);
                    }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
              );
            })
          )}
        </Stack>

        <Divider sx={{ mb: 2.5 }} />

        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.25 }}>
          {draft.id ? "Edit template" : "New template"}
        </Typography>
        <Stack spacing={2.25}>
          <CrmSelect
            label="Sequence step"
            value={draft.step}
            onChange={(e) => setDraft({ ...draft, step: e.target.value })}
            options={STEPS}
          />
          <TextField
            label="Template name"
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            fullWidth
            sx={fieldSx}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Subject"
            value={draft.subject}
            onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
            fullWidth
            sx={fieldSx}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Email body (HTML)"
            value={draft.html}
            onChange={(e) => setDraft({ ...draft, html: e.target.value })}
            fullWidth
            multiline
            minRows={8}
            InputLabelProps={{ shrink: true }}
            sx={{
              "& .MuiOutlinedInput-root": { borderRadius: 2 },
              "& textarea": { fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 13 },
            }}
          />
          {error ? (
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={() => setDraft(EMPTY_DRAFT)} sx={{ color: "#334155 !important", fontWeight: 700 }}>
          New template
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} sx={{ color: "#334155 !important", fontWeight: 700 }}>
          Close
        </Button>
        <Button
          variant="contained"
          onClick={save}
          disabled={!draft.name.trim() || !draft.subject.trim()}
          sx={{ fontWeight: 700, color: "#fff !important" }}
        >
          {draft.id ? "Save template" : "Add template"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function SendEmailDialog({ open, row, onClose, onSent }) {
  const [templates, setTemplates] = useState([]);
  const [step, setStep] = useState("first");
  const [templateId, setTemplateId] = useState("");
  const [subject, setSubject] = useState("");
  const [html, setHtml] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const stepSummary = row?.email_summary?.steps || {};
  const firstSent =
    Boolean(row?.email_summary?.first_sent) ||
    (stepSummary.first?.sent_count || 0) > 0 ||
    stepSummary.first?.unlocked === true;
  const stepOptions = STEPS.map((item) => {
    const unlocked = item.value === "first" || firstSent || stepSummary[item.value]?.unlocked;
    return {
      ...item,
      label: unlocked ? item.label : `${item.label} (locked)`,
      disabled: !unlocked,
    };
  });

  useEffect(() => {
    if (!open) return;
    setError("");
    setTemplateId("");
    setSubject("");
    setHtml("");
    // Prefer next unlocked unsent step after first email is out.
    const preferred =
      STEPS.find((item) => {
        const unlocked = item.value === "first" || firstSent || stepSummary[item.value]?.unlocked;
        const sent = (stepSummary[item.value]?.sent_count || 0) > 0;
        return unlocked && !sent;
      })?.value || "first";
    setStep(preferred);
    GetCrmTemplatesCall().then((res) => {
      const list = Array.isArray(res?.data) ? res.data : [];
      setTemplates(list);
      const match =
        list.find((item) => item.step === preferred) ||
        list.find((item) => item.step === "first") ||
        list[0];
      if (match) {
        setTemplateId(match.id);
        setSubject(match.subject || "");
        setHtml(match.html || "");
        if (match.step && (match.step === "first" || firstSent)) {
          setStep(match.step);
        }
      }
    });
  }, [open, row?.id]);

  const applyTemplate = (id) => {
    setTemplateId(id);
    const template = templates.find((item) => item.id === id);
    if (!template) return;
    setSubject(template.subject || "");
    setHtml(template.html || "");
    if (template.step) {
      const unlocked =
        template.step === "first" || firstSent || stepSummary[template.step]?.unlocked;
      if (unlocked) setStep(template.step);
    }
  };

  const send = async () => {
    if (!row) return;
    const unlocked = step === "first" || firstSent || stepSummary[step]?.unlocked;
    if (!unlocked) {
      setError("Send the First email before unlocking Reminder 1–3.");
      return;
    }
    setSending(true);
    setError("");
    const res = await SendCrmEmailCall(row.id, { step, template_id: templateId || null, subject, html });
    setSending(false);
    if (res?.success) {
      onSent();
      onClose();
      return;
    }
    setError(res?.message || "Email was not sent.");
  };

  const templateOptions = templates.map((template) => ({
    value: template.id,
    label: template.name,
  }));

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Email {row?.first_name || row?.work_email || "customer"}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          From samira@microlab.ink. Saeid gets a separate CRM copy (no open pixel). Reminder 1–3 unlock after First email. Green = open tracked after a short delay (prefetch/CC opens are ignored).
        </Typography>
        <Stack spacing={2}>
          <CrmSelect
            label="Sequence step"
            value={step}
            onChange={(e) => setStep(e.target.value)}
            options={stepOptions}
          />
          <CrmSelect
            label="Template"
            value={templateId}
            onChange={(e) => applyTemplate(e.target.value)}
            options={templateOptions}
            emptyLabel="Select a template"
          />
          <TextField
            label="Subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            fullWidth
            sx={fieldSx}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Email body (HTML)"
            value={html}
            onChange={(e) => setHtml(e.target.value)}
            fullWidth
            multiline
            minRows={8}
            InputLabelProps={{ shrink: true }}
            sx={{
              "& .MuiOutlinedInput-root": { borderRadius: 2 },
              "& textarea": { fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 13 },
            }}
          />
          {error ? (
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ color: "#334155 !important", fontWeight: 700 }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={sending || !subject.trim() || !html.trim()}
          onClick={send}
          sx={{ fontWeight: 700, color: "#fff !important" }}
        >
          {sending ? "Sending…" : "Send"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
